import { z } from "zod";
import { competenciaSchema, dataSchema } from "./competencia";

export const DIRECOES_DA_NOTA = ["EMITIDA", "RECEBIDA"] as const;
export type DirecaoDaNota = (typeof DIRECOES_DA_NOTA)[number];
export const DIRECAO_LABELS: Record<DirecaoDaNota, string> = { EMITIDA: "Emitida", RECEBIDA: "Recebida" };

/** PDF, XML e imagem (nota fotografada). */
export const TIPOS_DE_ARQUIVO_DA_NOTA = ["application/pdf", "application/xml", "text/xml", "image/jpeg", "image/png"] as const;
export const MAX_ARQUIVO_DA_NOTA = 10 * 1024 * 1024;

export const notaPedirUploadSchema = z.object({
  fileName: z.string().trim().min(1).max(200),
  contentType: z.enum(TIPOS_DE_ARQUIVO_DA_NOTA),
  contentLength: z.number().int().positive().max(MAX_ARQUIVO_DA_NOTA, "Arquivo maior que 10 MB"),
});

export const notaSalvarSchema = z.object({
  id: z.string().min(1).optional(),
  direcao: z.enum(DIRECOES_DA_NOTA),
  numero: z.string().trim().min(1, "Informe o número").max(30),
  serie: z.string().trim().max(10).nullable().optional(),
  chaveDeAcesso: z.string().trim().max(60).nullable().optional(),
  emitidaEm: dataSchema,
  valorCents: z.number().int().positive("Informe o valor"),
  partnerId: z.string().min(1).nullable().optional(),
  contraparte: z.string().trim().max(200).nullable().optional(),
  observacao: z.string().trim().max(1000).nullable().optional(),
  lancamentoIds: z.array(z.string().min(1)).max(50).default([]),
  fileIds: z.array(z.string().min(1)).max(5).default([]),
});
export type NotaSalvarInput = z.infer<typeof notaSalvarSchema>;

export const notaListarSchema = z.object({ competencia: competenciaSchema });

/** A nota que vem junto no "Novo lançamento": número e arquivo (o XML, se houver, já preencheu o resto). */
export const notaDoLancamentoSchema = z.object({
  numero: z.string().trim().min(1, "Informe o número da nota").max(30),
  serie: z.string().trim().max(10).nullable().optional(),
  chaveDeAcesso: z.string().trim().max(60).nullable().optional(),
  emitidaEm: dataSchema,
  fileIds: z.array(z.string().min(1)).max(5).default([]),
});
export type NotaDoLancamentoInput = z.infer<typeof notaDoLancamentoSchema>;

// ——— Importação de notas de fornecedor em lote (histórico) ———

const documentoSchema = z.string().trim().max(20).nullable();

export const notaPreviaSchema = z.object({
  itens: z
    .array(
      z.object({
        chaveDeAcesso: z.string().trim().max(60).nullable(),
        numero: z.string().trim().min(1).max(30),
        documento: documentoSchema,
        valorCents: z.number().int().positive(),
        emitidaEm: dataSchema,
      }),
    )
    .max(200),
});
export type NotaPreviaInput = z.infer<typeof notaPreviaSchema>;

export const DESPESAS_DA_IMPORTACAO = ["nova", "ligar", "nenhuma"] as const;

export const notaImportarSchema = z.object({
  notas: z
    .array(
      z.object({
        numero: z.string().trim().min(1).max(30),
        serie: z.string().trim().max(10).nullable().optional(),
        chaveDeAcesso: z.string().trim().max(60).nullable().optional(),
        emitidaEm: dataSchema,
        valorCents: z.number().int().positive(),
        fornecedor: z.object({ nome: z.string().trim().min(1).max(160), documento: documentoSchema }),
        /** Sem cadastro com esse documento: cria o fornecedor (nome e documento do XML). */
        criarFornecedor: z.boolean(),
        /** Só conta na despesa "nova"; nas outras pode vir vazio. */
        categoriaId: z.string().min(1).nullable(),
        despesa: z.enum(DESPESAS_DA_IMPORTACAO),
        lancamentoId: z.string().min(1).nullable().optional(),
        fileId: z.string().min(1),
      }),
    )
    .min(1)
    .max(50),
});
export type NotaImportarInput = z.infer<typeof notaImportarSchema>;

export type ResultadoDaImportacao = "importada" | "jaRegistrada" | "mesFechado" | "erro";
